import {
  ArrowLeft,
  Building2,
  Gavel,
  Mail,
  Plus,
  Phone,
  Save,
  Trash2,
  Users,
  Globe,
  FileText,
  Download,
  LoaderCircle,
  Search,
  Filter,
  ArrowUpDown
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { SupportTicketsPanel, type LinkedSupportTicket } from "../../components/SupportTicketsPanel";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { CreateUserModal } from "../../components/CreateUserModal";
import { AddExistingUserModal } from "../../components/AddExistingUserModal";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Member = {
  id: string;
  role: string;
  createdAt: string;
  userId: string;
  name: string;
  email: string | null;
  phoneNumber: string | null;
};

type Auction = {
  id: string;
  title: string;
  status: string;
  currentBid: number;
  bidCount: number;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  make: string | null;
  model: string | null;
  modelYear: number | null;
};

type Document = {
  id: string;
  title: string;
  source?: "document" | "accounting";
  module: string;
  documentType: string;
  status: string;
  createdAt: string;
  templateName: string | null;
  creatorName: string | null;
  signedFileKey: string | null;
  documentNumber?: string | null;
  customerName?: string | null;
};

type OrgDetail = {
  id: string;
  name: string;
  slug: string;
  capability: "full_marketplace" | "seller_only";
  orgNumber: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  billingAddress: string | null;
  billingCity: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  invoicingMethod: string | null;
  invoicingEmail: string | null;
  bankAccountNumber: string | null;
  pendingBankAccountNumber: string | null;
  shippingAddresses: ShippingAddress[] | null;
  createdAt: string;
  updatedAt: string;
  members: Member[];
  auctions: Auction[];
  supportTickets: LinkedSupportTicket[];
};

type ShippingAddress = {
  label?: string;
  address: string;
  postalCode: string;
  city: string;
  contactPhone?: string;
};

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<OrgDetail>>({});

  const [bankAccountAction, setBankAccountAction] = useState<"approve" | "reject" | null>(null);

  // Documents Tab State
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docSearchQuery, setDocSearchQuery] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddExistingOpen, setIsAddExistingOpen] = useState(false);

  const fetchOrgData = () => {
    if (!id) return;
    setLoading(true);
    fetch(`${API}/api/admin/organizations/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setData(d);
        if (d) setForm(d);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrgData();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoadingDocs(true);
    fetch(`${API}/api/admin/documents?organizationId=${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setDocuments(Array.isArray(d) ? d : []))
      .catch((err) => console.error("Failed to fetch documents:", err))
      .finally(() => setLoadingDocs(false));
  }, [id]);

  const handleDownload = async (doc: Document) => {
    try {
      setDownloadingId(doc.id);
      const variant = doc.status === "signed" ? "signed" : "unsigned";
      const params = new URLSearchParams({ variant });
      if (doc.source === "accounting") {
        params.set("source", "accounting");
      }

      const response = await fetch(`${API}/api/admin/documents/${doc.id}/download-url?${params.toString()}`, {
        credentials: "include"
      });

      if (!response.ok) throw new Error("Kunne ikke hente nedlastingslenke");

      const { downloadUrl } = await response.json();

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${doc.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Nedlasting feilet:", err);
      alert("Kunne ikke laste ned dokumentet.");
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredDocuments = documents.filter((doc) =>
    doc.title.toLowerCase().includes(docSearchQuery.toLowerCase()) ||
    doc.templateName?.toLowerCase().includes(docSearchQuery.toLowerCase()) ||
    doc.documentNumber?.toLowerCase().includes(docSearchQuery.toLowerCase()) ||
    doc.customerName?.toLowerCase().includes(docSearchQuery.toLowerCase())
  );

  async function handleSave() {
    if (!id) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/organizations/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          capability: form.capability,
          orgNumber: form.orgNumber,
          billingAddress: form.billingAddress,
          billingCity: form.billingCity,
          billingPostalCode: form.billingPostalCode,
          invoicingMethod: form.invoicingMethod,
          invoicingEmail: form.invoicingEmail,
          bankAccountNumber: form.bankAccountNumber || undefined,
          shippingAddresses: form.shippingAddresses,
        }),
      });
      if (r.ok) {
        const updated = await r.json();
        setData((prev) => prev ? { ...prev, ...updated } : prev);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleBankAccountAction(action: "approve" | "reject") {
    if (!id) return;
    setBankAccountAction(action);
    try {
      await fetch(`${API}/api/admin/organizations/${id}/${action}-bank-account`, {
        method: "POST",
        credentials: "include",
      });
      fetchOrgData();
    } finally {
      setBankAccountAction(null);
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <p className="text-sm text-muted-foreground">Laster organisasjon...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <p className="text-sm text-destructive">Organisasjonen ble ikke funnet.</p>
      </div>
    );
  }

  const field = (key: keyof OrgDetail) => (
    <input
      className="hidden"
      value={form[key] as string ?? ""}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
    />
  );
  void field;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/organisasjoner")} className="mt-0.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/8 flex-shrink-0">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
                {data.name}
              </h1>
              <p className="text-sm text-muted-foreground font-mono">{data.slug}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-shrink-0">
          <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{data.members.length}</span>
          <span className="flex items-center gap-1.5"><Gavel className="h-4 w-4" />{data.auctions.length}</span>
          <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{data.supportTickets.length}</span>
          <span className="flex items-center gap-1.5"><FileText className="h-4 w-4" />{documents.length}</span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="oversikt">
        <TabsList>
          <TabsTrigger value="oversikt">Oversikt</TabsTrigger>
          <TabsTrigger value="brukere">Brukere ({data.members.length})</TabsTrigger>
          <TabsTrigger value="auksjoner">Auksjoner ({data.auctions.length})</TabsTrigger>
          <TabsTrigger value="support">Support ({data.supportTickets.length})</TabsTrigger>
          <TabsTrigger value="dokumenter">Dokumenter ({documents.length})</TabsTrigger>
          <TabsTrigger value="innstillinger">Innstillinger</TabsTrigger>
        </TabsList>

        {/* Oversikt */}
        <TabsContent value="oversikt" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={<Users className="h-4 w-4" />} label="Brukere" value={data.members.length} />
            <StatCard icon={<Gavel className="h-4 w-4" />} label="Auksjoner" value={data.auctions.length} />
            <StatCard icon={<Mail className="h-4 w-4" />} label="Support" value={data.supportTickets.length} />
            <StatCard icon={<FileText className="h-4 w-4" />} label="Dokumenter" value={documents.length} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-sm font-semibold">Kontaktinfo</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.email && <InfoRow icon={<Mail className="h-3.5 w-3.5" />} value={data.email} />}
                {data.phone && <InfoRow icon={<Phone className="h-3.5 w-3.5" />} value={data.phone} />}
                {data.website && <InfoRow icon={<Globe className="h-3.5 w-3.5" />} value={data.website} />}
                {!data.email && !data.phone && !data.website && (
                  <p className="text-muted-foreground">Ingen kontaktinfo registrert</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm font-semibold">Fakturering</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Metode</span>
                  <span>{data.invoicingMethod === "ehf" ? "EHF" : "E-post"}</span>
                </div>
                {data.invoicingEmail && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">E-post</span>
                    <span className="truncate ml-4">{data.invoicingEmail}</span>
                  </div>
                )}
                {data.billingAddress && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Adresse</span>
                    <span className="truncate ml-4">{[data.billingAddress, data.billingPostalCode, data.billingCity].filter(Boolean).join(", ")}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Kontonummer</span>
                  <span className="font-mono">{data.bankAccountNumber ?? "—"}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                  <span>Opprettet</span>
                  <span>{formatDate(data.createdAt)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
          {data.pendingBankAccountNumber && (
            <Card className="border-amber-300 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-amber-800">Venter på godkjenning — kontonummer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Innsendt kontonummer</span>
                  <span className="font-mono font-semibold">{data.pendingBankAccountNumber}</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={() => handleBankAccountAction("approve")}
                    disabled={bankAccountAction !== null}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {bankAccountAction === "approve" ? "Godkjenner..." : "Godkjenn"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBankAccountAction("reject")}
                    disabled={bankAccountAction !== null}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    {bankAccountAction === "reject" ? "Avviser..." : "Avvis"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Brukere */}
        <TabsContent value="brukere" className="mt-6 space-y-4">
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsAddExistingOpen(true)} size="sm" variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Legg til eksisterende bruker
            </Button>
            <Button onClick={() => setIsModalOpen(true)} size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              Opprett ny bruker
            </Button>
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Navn</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">E-post</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Telefon</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rolle</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Lagt til</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.members.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Ingen brukere</td></tr>
                ) : (
                  data.members.map((m) => (
                    <tr key={m.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-3.5 font-medium">{m.name}</td>
                      <td className="px-6 py-3.5 text-muted-foreground">{m.email ?? "—"}</td>
                      <td className="px-6 py-3.5 text-muted-foreground hidden sm:table-cell">{m.phoneNumber ?? "—"}</td>
                      <td className="px-6 py-3.5"><RoleBadge role={m.role} /></td>
                      <td className="px-6 py-3.5 text-muted-foreground hidden md:table-cell">{formatDate(m.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <CreateUserModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={fetchOrgData}
          initialOrganizationId={id}
        />

        <AddExistingUserModal
          isOpen={isAddExistingOpen}
          organizationId={id!}
          organizationName={data.name}
          onClose={() => setIsAddExistingOpen(false)}
          onSuccess={fetchOrgData}
        />

        {/* Auksjoner */}
        <TabsContent value="auksjoner" className="mt-6">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Auksjon</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Bud</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Ant. bud</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Slutter</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.auctions.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Ingen auksjoner</td></tr>
                ) : (
                  data.auctions.map((a) => (
                    <tr key={a.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-3.5">
                        <p className="font-medium">{a.title}</p>
                        {(a.make || a.model) && (
                          <p className="text-xs text-muted-foreground">{[a.modelYear, a.make, a.model].filter(Boolean).join(" ")}</p>
                        )}
                      </td>
                      <td className="px-6 py-3.5"><AuctionStatusBadge status={a.status} /></td>
                      <td className="px-6 py-3.5 text-right hidden sm:table-cell">{a.currentBid > 0 ? `${a.currentBid.toLocaleString("nb-NO")} kr` : "—"}</td>
                      <td className="px-6 py-3.5 text-right hidden sm:table-cell">{a.bidCount}</td>
                      <td className="px-6 py-3.5 text-muted-foreground hidden md:table-cell">{a.endsAt ? formatDate(a.endsAt) : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="support" className="mt-6">
          <SupportTicketsPanel
            tickets={data.supportTickets}
            emptyLabel="Ingen supporthenvendelser er koblet til denne organisasjonen."
          />
        </TabsContent>

        {/* Dokumenter */}
        <TabsContent value="dokumenter" className="mt-6 space-y-4">
          <Card className="border-slate-200/60 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-slate-50/30 flex flex-col sm:flex-row gap-4 justify-between items-center">
              <div className="relative w-full sm:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Søk i dokumenter..."
                  className="pl-10 bg-white border-slate-200 focus-visible:ring-primary/20"
                  value={docSearchQuery}
                  onChange={(e) => setDocSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="text-slate-600 font-medium">
                  <Filter className="mr-2 h-4 w-4" />
                  Filtrer
                </Button>
                <Button variant="outline" size="sm" className="text-slate-600 font-medium">
                  <ArrowUpDown className="mr-2 h-4 w-4" />
                  Sorter
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <th className="px-6 py-4 font-semibold text-slate-900">Tittel</th>
                    <th className="px-6 py-4 font-semibold text-slate-900">Mal</th>
                    <th className="px-6 py-4 font-semibold text-slate-900">Status</th>
                    <th className="px-6 py-4 font-semibold text-slate-900 text-right">Dato</th>
                    <th className="px-6 py-4 font-semibold text-slate-900 text-right">Handlinger</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingDocs ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <LoaderCircle className="h-6 w-6 animate-spin mx-auto text-primary/20" />
                      </td>
                    </tr>
                  ) : filteredDocuments.length > 0 ? (
                    filteredDocuments.map((doc) => (
                      <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-white group-hover:shadow-sm transition-all">
                              <FileText className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{doc.title}</p>
                              <p className="text-[11px] text-slate-500 font-mono tracking-tighter uppercase">{doc.id.split('-')[0]}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-600">{doc.templateName || "Egendefinert"}</span>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(doc.status)}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-500">
                          <div className="flex flex-col items-end">
                            <span>{new Date(doc.createdAt).toLocaleDateString("nb-NO")}</span>
                            <span className="text-[11px] opacity-60">
                              {new Date(doc.createdAt).toLocaleTimeString("nb-NO", { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                              disabled={downloadingId === doc.id}
                              onClick={() => handleDownload(doc)}
                              title="Last ned"
                            >
                              {downloadingId === doc.id ? (
                                <LoaderCircle className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                        Ingen dokumenter funnet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Innstillinger */}
        <TabsContent value="innstillinger" className="mt-6 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Generelt</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Navn" value={form.name ?? ""} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
              <FormField label="Org.nr" value={form.orgNumber ?? ""} onChange={(v) => setForm((f) => ({ ...f, orgNumber: v }))} />
              <FormField label="E-post" value={form.email ?? ""} onChange={(v) => setForm((f) => ({ ...f, email: v }))} type="email" />
              <FormField label="Telefon" value={form.phone ?? ""} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Organisasjonstilgang</Label>
                <div className="flex gap-2">
                  {[
                    { id: "full_marketplace" as const, label: "Full markedsplass" },
                    { id: "seller_only" as const, label: "Kun selger" },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, capability: option.id }))}
                      className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                        form.capability === option.id
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border hover:bg-muted/40"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Fakturaadresse</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <FormField label="Fakturaadresse" value={form.billingAddress ?? ""} onChange={(v) => setForm((f) => ({ ...f, billingAddress: v }))} />
              </div>
              <FormField label="Postnummer" value={form.billingPostalCode ?? ""} onChange={(v) => setForm((f) => ({ ...f, billingPostalCode: v }))} />
              <FormField label="Poststed" value={form.billingCity ?? ""} onChange={(v) => setForm((f) => ({ ...f, billingCity: v }))} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Utbetalingskonto</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Kontonummer (admin-overstyring)" value={form.bankAccountNumber ?? ""} onChange={(v) => setForm((f) => ({ ...f, bankAccountNumber: v }))} />
              <p className="text-xs text-muted-foreground sm:col-span-2">Skriv inn kontonummer direkte som admin-overstyring (krever ikke godkjenningsflyt).</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Fakturering</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Faktureringsmetode</Label>
                <div className="flex gap-2">
                  {["email", "ehf"].map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, invoicingMethod: method }))}
                      className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${form.invoicingMethod === method ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/40"}`}
                    >
                      {method === "email" ? "E-post" : "EHF"}
                    </button>
                  ))}
                </div>
              </div>
              <FormField label="Faktura e-post" value={form.invoicingEmail ?? ""} onChange={(v) => setForm((f) => ({ ...f, invoicingEmail: v }))} type="email" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-sm font-semibold">Leveringsadresser</CardTitle>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="gap-2"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      shippingAddresses: [
                        ...(f.shippingAddresses ?? []),
                        { label: "", address: "", postalCode: "", city: "", contactPhone: "" },
                      ],
                    }))
                  }
                >
                  <Plus className="h-4 w-4" />
                  Legg til adresse
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(form.shippingAddresses?.length ?? 0) === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
                  Ingen leveringsadresser lagt til.
                </div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  {(form.shippingAddresses ?? []).map((address, index) => (
                    <ShippingAddressCard
                      key={`${index}-${address.label ?? address.address}`}
                      value={address}
                      onChange={(nextAddress) =>
                        setForm((f) => ({
                          ...f,
                          shippingAddresses: (f.shippingAddresses ?? []).map((item, itemIndex) =>
                            itemIndex === index ? nextAddress : item
                          ),
                        }))
                      }
                      onRemove={() =>
                        setForm((f) => ({
                          ...f,
                          shippingAddresses: (f.shippingAddresses ?? []).filter((_, itemIndex) => itemIndex !== index),
                        }))
                      }
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              {saving ? "Lagrer..." : "Lagre endringer"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/8 text-primary flex-shrink-0">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-bold">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function InfoRow({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground flex-shrink-0">{icon}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function ShippingAddressCard({
  value,
  onChange,
  onRemove,
}: {
  value: ShippingAddress;
  onChange: (value: ShippingAddress) => void;
  onRemove: () => void;
}) {
  return (
    <div className="relative rounded-2xl border border-border bg-background p-4 shadow-sm">
      <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-2 h-8 w-8" onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
      </Button>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Navn/label" value={value.label ?? ""} onChange={(label) => onChange({ ...value, label })} />
        <FormField label="Kontakt (tlf)" value={value.contactPhone ?? ""} onChange={(contactPhone) => onChange({ ...value, contactPhone })} />
      </div>
      <div className="mt-4">
        <FormField label="Adresse" value={value.address} onChange={(address) => onChange({ ...value, address })} />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <FormField label="Postnummer" value={value.postalCode} onChange={(postalCode) => onChange({ ...value, postalCode })} />
        <FormField label="Poststed" value={value.city} onChange={(city) => onChange({ ...value, city })} />
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    owner: { label: "Eier", variant: "accent" },
    admin: { label: "Admin", variant: "warning" },
    member: { label: "Bruker", variant: "secondary" },
  };
  const { label, variant } = map[role] ?? { label: role, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function AuctionStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    draft: { label: "Utkast", variant: "muted" },
    pending_review: { label: "Til godkjenning", variant: "warning" },
    published: { label: "Aktiv", variant: "success" },
    closed: { label: "Avsluttet", variant: "secondary" },
    cancelled: { label: "Kansellert", variant: "destructive" },
  };
  const { label, variant } = map[status] ?? { label: status, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function getStatusBadge(status: string) {
  switch (status.toLowerCase()) {
    case "signed":
      return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Signert</Badge>;
    case "draft":
      return <Badge variant="secondary" className="bg-slate-100 text-slate-600">Utkast</Badge>;
    case "pending":
      return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">Venter</Badge>;
    default:
      return <Badge variant="muted">{status}</Badge>;
  }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
